import { CheckOutlined, CloseOutlined } from '@mui/icons-material';
import { ChangeEvent, useEffect, useState } from 'react';
import { Dayjs } from 'dayjs';
import { SelectChangeEvent } from '@mui/material';

import { CustomInput, CustomSelect } from '../../../../../shared/uiComponents';
import { validateDecimalNumber, validateNumber } from '../../../../../shared/Helpers/functions';
import { EditSvg } from '../../../../../shared/Assets/svgIcons';
import Delete from '../../../../../components/Delete';
import { useCalculateHoursPerWeek } from '../hook';
import { EditAuthorizationProperties } from './editAuthorizationContent';

import { useSelector } from '../../../../../redux/store';

import './index.scss';

const AuthorizationDetail = ({
	item,
	startDateValue,
	endDateValue,
	update,
	remove,
	authorizationTypeId,
}: {
	authorizationTypeId: number;
	endDateValue: Dayjs;
	item: EditAuthorizationProperties;
	remove: (item: EditAuthorizationProperties) => void;
	startDateValue: Dayjs;
	update: (item: EditAuthorizationProperties) => void;
}) => {
	const [typeId, setTypeId] = useState<string>(item.authorizationSessionTypeId);
	const [frequency, setFrequency] = useState<number>(item.frequency);
	const [totalHours, setTotalHours] = useState<string>(`${item.totalHours}`);
	const [customHoursPerWeek, setCustomHoursPerWeek] = useState<string>(item.hoursPerWeek ? `${item.hoursPerWeek}` : '');
	const [edit, setEdit] = useState<boolean>(false);
	const { hoursPerWeek, loading } = useCalculateHoursPerWeek({
		startDateValue,
		endDateValue,
		totalHours,
		frequency,
	});

	const sessionTypes = useSelector((state) => state.authorization.insuranceAuthorizationSessionTypes);

	const onChangeSessionType = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setTypeId(value);
		const frequencyId = sessionTypes.find((x) => x.id === value)?.frequencies[0].id;
		if (!frequencyId) return;
		setFrequency(frequencyId);
		setTotalHours('0');
	};

	const frequencyHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		if (!validateNumber(value)) return;
		setFrequency(parseInt(value));
	};

	const onTotalHoursChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		if (!validateDecimalNumber(value)) return;
		setTotalHours(value);
		setCustomHoursPerWeek('');
	};

	const onHoursPerWeekChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		if (!validateDecimalNumber(value)) return;
		setCustomHoursPerWeek(value);
	};

	const addDetailHandler = () => {
		const data: EditAuthorizationProperties = {
			id: item.id,
			authorizationSessionTypeId: typeId,
			frequency,
			totalHours: parseFloat(totalHours),
			hoursPerWeek: frequency === 1 ? parseFloat(customHoursPerWeek) : null,
		};
		update(data);
		setEdit(false);
	};

	const removeAuthorizationDetailHandler = () => remove(item);

	const cancelEditAuthorizationHandler = () => {
		setEdit(false);
		setTypeId(item.authorizationSessionTypeId);
		setFrequency(item.frequency);
		setTotalHours(`${item.totalHours}`);
		setCustomHoursPerWeek('');
	};

	useEffect(() => {
		if (!totalHours.length) return;

		if (item.totalHours === parseFloat(totalHours)) {
			setCustomHoursPerWeek(`${item.hoursPerWeek}`);

			return;
		}
		setCustomHoursPerWeek(`${hoursPerWeek}`);
	}, [item, totalHours, hoursPerWeek]);

	return (
		<div className="authorizationDetailCard padding8 marginBottom8">
			<div className="sessionTypeRow marginBottom8">
				<div className="fullWidth">
					<CustomSelect
						label="Choose session type:"
						data={sessionTypes}
						value={typeId}
						setValue={onChangeSessionType}
						className="marginBottom16"
						disabled={!edit}
					/>
				</div>
				<CustomSelect
					label="Frequency"
					data={
						sessionTypes
							.find((x) => x.id === typeId)
							?.frequencies.map((x) => ({
								id: `${x.id}`,
								label: x.name,
							})) as any[]
					}
					value={`${frequency}`}
					disabled={!edit}
					setValue={frequencyHandler}
					className="width150"
				/>
			</div>
			<div className="hoursRow marginBottom8">
				<CustomInput label="Total hours" disabled={!edit} value={totalHours} onChange={onTotalHoursChange} />
				{frequency === 1 && (
					<CustomInput
						label="Weekly limit"
						disabled={!edit}
						value={customHoursPerWeek}
						onChange={onHoursPerWeekChange}
					/>
				)}
				{!loading && (
					<div className="detailActions marginTop24">
						{edit ? (
							Boolean(parseFloat(totalHours)) && (
								<>
									<CheckOutlined fontSize="small" onClick={addDetailHandler} />
									<CloseOutlined fontSize="small" onClick={cancelEditAuthorizationHandler} />
								</>
							)
						) : (
							<div onClick={() => setEdit(true)}>
								<EditSvg />
							</div>
						)}
						{authorizationTypeId === 2 && <Delete deleteHandler={removeAuthorizationDetailHandler} />}
					</div>
				)}
			</div>
		</div>
	);
};

export default AuthorizationDetail;
